<template>
  <div class="main_profile_wrapper">
    <div class="answers_container">
      <div class="d-flex align-items-center justify-content-between mb-4 header_bar">
        <div>
          <h2 class="title font-weight-bold m-0 text-dark">{{ localization('Student Answers') }}</h2>
          <p class="text-muted m-0 small">{{ localization('Review submitted answers for group activities.') }}</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <el-button type="primary" size="medium" @click="printAnswers">
            <i class="fa fa-print mr-1"></i> {{ localization('Print') }}
          </el-button>
          <el-button 
            type="success" 
            size="medium" 
            @click="printNotebook"
            :disabled="notebooksToShow.length === 0 && !hasPermission('print student notebook')"
          >
            <i class="fa fa-book mr-1"></i> {{ localization('Print Notebook') }}
          </el-button>
          <el-button size="medium" @click="goBack">
            <i class="fa fa-arrow-left mr-1"></i> {{ localization('Back') }}
          </el-button>
        </div>
      </div>

      <div v-if="loading" class="text-center p-5 my-5">
        <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
        <p class="text-muted mt-2">{{ localization('Loading student answers...') }}</p>
      </div>

      <div v-else-if="member" class="content_grid">
      <!-- Member Info Card -->
      <el-card class="box-card mb-4 member_info_card shadow-sm border-0">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div class="d-flex align-items-center">
            <div class="avatar_icon mr-3 text-white d-flex align-items-center justify-content-center font-weight-bold">
              {{ (member.first_name || 'U')[0].toUpperCase() }}
            </div>
            <div>
              <h3 class="m-0 font-weight-bold text-dark">{{ member.first_name }} {{ member.last_name }}</h3>
              <p class="m-0 text-muted small">{{ member.email }}</p>
            </div>
          </div>
          <div class="text-right d-flex flex-column align-items-end">
            <el-tag
              :type="member.status === 'completed' ? 'success' : member.status === 'answered' ? 'primary' : 'warning'"
              size="medium"
              class="mb-1 font-weight-bold"
            >
              {{ localization(member.status || 'new') }}
            </el-tag>
            <span class="text-muted small">{{ localization('Group') }}: <strong>{{ groupName }}</strong></span>
          </div>
        </div>
      </el-card>

      <!-- LCI Scores Card Grid -->
      <div class="mb-4">
        <UserPattern :userScore="member" :withTitle="true" :hideActions="true"></UserPattern>
      </div>

      <!-- Student Notebook Section -->
      <div v-if="notebooksToShow.length > 0" class="mb-4">
        <el-card
          v-for="subtopic in notebooksToShow"
          :key="'notebook-' + subtopic.id"
          class="mb-4 student_notebook_card shadow-sm border rounded"
        >
          <div slot="header" class="clearfix d-flex align-items-center justify-content-between">
            <h3 class="notebook_card_title m-0 font-weight-bold text-dark d-flex align-items-center" style="font-size: 1.25rem;">
              <i class="fa fa-book text-primary mr-2" style="color: #7f56d9;"></i>
              {{ subtopic.student_notebook_title || subtopic.name || localization('Students Notebook') }}
            </h3>
            <el-tag size="small" type="primary" v-if="schoolGrade">
              {{ localization('Grade') }} {{ schoolGrade }}
            </el-tag>
          </div>
          <div 
            class="student_notebook_wrapper"
            v-html="formatNotebookHtml(subtopic.student_notebook)"
          ></div>
        </el-card>
      </div>

      <!-- Personalize Your Profile Section (Sequence - Precision - Technical Reasoning - Confluence) -->
      <div v-if="userAttributes" class="global_theme_ui_collapse mb-4 shadow-sm bg-white p-4 rounded border">
        <h2 class="main_title font-weight-bold mb-1 text-dark" style="font-size: 1.25rem;">
          {{ localization("Personalize your profile") }}
        </h2>
        <p class="text-muted small mb-4">
          {{ localization("Your choices provide insights about how you learn, work, and interact with others.") }}
        </p>

        <el-collapse
          class="customization_for_collapse"
          v-model="activePanels"
          multiple
        >
          <div
            class="main_child"
            v-for="(attribute, key) in userAttributes"
            :key="key"
            :style="`border: 2px solid ${getProgressColor(key)}; border-radius: 16px; margin-bottom: 20px; padding: 15px;`"
          >
            <!-- Start Collapse Title -->
            <el-collapse-item
              :name="`${key}`"
              :class="{ break_section: key === 'Precision' }"
            >
              <template #title>
                <div class="custom_header_of_collapse d-flex align-items-center w-100 pb-2">
                  <img
                    src="@/assets/new-theme/attributes-images/sequence.png"
                    alt="sequence"
                    v-if="key === 'Sequence'"
                    style="width: 24px; height: 24px;"
                  />
                  <img
                    src="@/assets/new-theme/attributes-images/precision.png"
                    alt="precision"
                    v-if="key === 'Precision'"
                    style="width: 24px; height: 24px;"
                  />
                  <img
                    src="@/assets/new-theme/attributes-images/technical_reasoning.png"
                    alt="technical_reasoning"
                    v-if="key === 'Technical Reasoning'"
                    style="width: 24px; height: 24px;"
                  />
                  <img
                    src="@/assets/new-theme/attributes-images/confluence.png"
                    alt="confluence"
                    v-if="key === 'Confluence'"
                    style="width: 24px; height: 24px;"
                  />
                  <span class="title_word font-weight-bold ml-2 text-dark" style="font-size: 1.05rem;">
                    {{ localization(key) }} - {{ localization(patternKey(key)) }}
                  </span>
                </div>
              </template>
              <!-- End Collapse Title -->
              <!-- Start Checkboxes -->
              <div class="p-3">
                <el-collapse class="collapse_checkboxes border-0">
                  <div
                    class="checkboxes_wrapper d-flex align-items-center justify-content-between mb-3"
                    v-for="(item, index) in attribute"
                    :key="index"
                  >
                    <el-checkbox
                      v-if="item"
                      :checked="isAttributeSelected(item.id)"
                      :disabled="true"
                      :class="{ 'd-none': !item.title || item.title.length < 1 }"
                      class="w-100"
                    >
                      <span :class="{'text-primary font-weight-bold': isAttributeSelected(item.id), 'text-dark font-weight-medium': !isAttributeSelected(item.id)}" style="font-size: 0.95rem; white-space: normal;">
                        {{ item.title }}
                      </span>
                    </el-checkbox>

                    <div class="d-flex align-items-center ml-2" v-if="item.title">
                      <span
                        class="span_img_wrapper"
                        role="button"
                        style="cursor: pointer;"
                        @click="text_speech_audio(item.title)"
                      >
                        <img
                          src="@/assets/new-theme/attributes-images/volume.png"
                          alt="volume"
                          style="width: 16px; height: 16px;"
                        />
                      </span>
                    </div>
                  </div>
                </el-collapse>
              </div>
              <!-- End Checkboxes  -->
            </el-collapse-item>
          </div>
        </el-collapse>
      </div>

      <audio controls id="audio-answers" class="d-none">
        <source id="audio-source-answers" type="audio/mp3" />
      </audio>

      <!-- Subtopics & Answers list -->
      <div v-if="subtopicsAnswers.length > 0">
        <div v-for="subtopic in subtopicsAnswers" :key="subtopic.id" class="mb-5 subtopic_block">
          <div class="subtopic_title_bar mb-3 p-3 rounded">
            <h3 class="m-0 font-weight-bold text-dark d-flex align-items-center">
              <i class="fa fa-folder-open text-primary mr-4"></i>
               {{ subtopic.name }}
            </h3>
            <p class="text-muted m-0 mt-1 pl-4 small" v-if="subtopic.description">{{ subtopic.description }}</p>
          </div>

          <div v-if="subtopic.components && subtopic.components.length > 0">
            <el-card 
              v-for="component in subtopic.components" 
              :key="component.id" 
              class="mb-3 component_card border-0 shadow-sm"
            >
              <div slot="header" class="d-flex align-items-center justify-content-between p-1">
                <span class="font-weight-bold text-dark">{{ component.title }}</span>
                <el-tag size="mini" type="info" class="text-uppercase">{{ localization('Component') }}</el-tag>
              </div>

              <div class="component_body mb-4 text-muted small" v-if="component.body" v-html="component.body"></div>

              <!-- Question Cards Inside Component -->
              <div v-if="(component.question_cards && component.question_cards.length > 0) || (isStrategiesComponent(component) && component.strategies_cards && component.strategies_cards.length > 0)" class="questions_list mt-3">
                <!-- Custom Strategies Component UI -->
                <div v-if="isStrategiesComponent(component)" class="strategies-component-container">
                  <!-- Render strategies_cards if available -->
                  <template v-if="component.strategies_cards && component.strategies_cards.length > 0">
                    <div 
                      v-for="(strategy, sIndex) in component.strategies_cards" 
                      :key="strategy.id || sIndex"
                      class="strategy-card-item mb-4"
                    >
                      <div class="strategy-banner" :style="{ backgroundColor: getStrategyColor(strategy.pattern) }">
                        <div class="strategy-pattern-name">{{ strategy.pattern }}</div>
                        <div class="strategy-main-text" v-html="strategy.name"></div>
                      </div>
                      <div class="strategy-description-box" v-if="strategy.description">
                        <div class="strategy-description-text" v-html="formatHelpText(strategy.description)"></div>
                      </div>
                    </div>
                  </template>
                  <!-- Fallback to question_cards if strategies_cards is empty -->
                  <template v-else>
                    <div 
                      v-for="(qCard, qIndex) in component.question_cards" 
                      :key="qCard.id || qIndex"
                      class="strategy-card-item mb-4"
                    >
                      <div class="strategy-banner" :style="{ backgroundColor: getStrategyColor(parseStrategyCard(qCard).pattern) }">
                        <div class="strategy-pattern-name">{{ parseStrategyCard(qCard).pattern }}</div>
                        <div class="strategy-main-text" v-html="parseStrategyCard(qCard).mainTitle"></div>
                      </div>
                      <div class="strategy-description-box" v-if="parseStrategyCard(qCard).description">
                        <div class="strategy-description-text" v-html="formatHelpText(parseStrategyCard(qCard).description)"></div>
                      </div>
                      <div class="strategy-student-answer-box" v-if="qCard.student_answer || qCard.answer">
                        <div class="strategy-student-answer-title">{{ localization('Answer') }}</div>
                        <div class="strategy-student-answer-text">{{ qCard.student_answer || qCard.answer }}</div>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Default Question Cards UI -->
                <template v-else>
                  <div 
                    v-for="(qCard, qIndex) in component.question_cards" 
                    :key="qCard.id"
                  >
                    <!-- MCQ Type -->
                    <div 
                      v-if="qCard.type === 'mcq' || (qCard.choices && qCard.choices.length > 0)"
                      class="p-3 mb-3 rounded border border-light bg-light-gray"
                    >
                      <div class="d-flex align-items-start mb-3">
                        <span class="badge badge-secondary mr-2 mt-1 text-white q_badge">
                          {{ qIndex + 1 }}
                        </span>
                        <span class="font-weight-bold text-dark text_question">
                          {{ qCard.question }}
                        </span>
                      </div>

                      <div class="choices_group pl-4">
                        <div 
                          v-for="choice in qCard.choices" 
                          :key="choice.id" 
                          class="choice_item d-flex align-items-center mb-2 p-2 rounded border"
                          :style="qCard.student_answer === choice.id ? 'background-color: #f9f5ff; border-color: #7f56d9; border-left: 4px solid #7f56d9;' : 'background-color: #ffffff; border-color: #eaecf0;'"
                        >
                          <i v-if="qCard.student_answer === choice.id" class="fa fa-dot-circle-o text-primary mr-2"></i>
                          <i v-else class="fa fa-circle-o text-muted mr-2"></i>
                          <span :class="{'text-primary font-weight-bold': qCard.student_answer === choice.id, 'text-secondary': qCard.student_answer !== choice.id}">
                            {{ choice.text }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Essay / Text Type styled card -->
                    <div v-else class="essay-question-container">
                      <div class="essay-question-header">
                        <div class="essay-step-label">
                          Step {{ parseQuestionCard(qCard, qIndex).stepNumber }}: <strong>{{ parseQuestionCard(qCard, qIndex).stepName }}</strong>
                        </div>
                        <div class="essay-main-question">
                          {{ parseQuestionCard(qCard, qIndex).mainQuestion }}
                        </div>
                        <div class="essay-help-text" v-if="parseQuestionCard(qCard, qIndex).helpText" v-html="formatHelpText(parseQuestionCard(qCard, qIndex).helpText)">
                        </div>
                      </div>
                      <div class="essay-question-body">
                        <div class="essay-answer-preview">
                          {{ qCard.student_answer || qCard.answer || localization('No answer submitted yet.') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </el-card>
          </div>
          <div v-else class="text-center p-4 bg-light rounded text-muted">
            {{ localization('No components or questions defined for this subtopic.') }}
          </div>
        </div>
      </div>
      <div v-else class="text-center p-5 bg-light rounded text-muted">
        {{ localization('No subtopics assigned to this group card.') }}
      </div>
    </div>
    </div>

    <!-- Hidden Print Layout Container -->
    <div v-if="printingMember" class="the_main_container_for_print_all_pdf bg-white print-only-block">
      <div class="main_container p-4">
        <!-- Notebook Only Mode -->
        <template v-if="printMode === 'notebook'">
          <div v-if="notebooksToShow.length > 0">
            <div 
              v-for="subtopic in notebooksToShow" 
              :key="'print-notebook-only-' + subtopic.id" 
              class="mb-4 student_notebook_print_card p-3 border rounded"
            >
              <h3 class="notebook_card_title font-weight-bold text-dark mb-3" style="font-size: 1.3rem; border-left: 4px solid #7f56d9; padding-left: 10px;">
                {{ subtopic.student_notebook_title || subtopic.name || localization('Students Notebook') }}
              </h3>
              <div 
                class="student_notebook_wrapper"
                v-html="formatNotebookHtml(subtopic.student_notebook)"
              ></div>
            </div>
          </div>
        </template>

        <!-- Full Print Mode (Answers + Notebook + LCI) -->
        <template v-else>
          <div class="main_auth_user_info text-center border-bottom pb-4 mb-4">
            <h2 class="n_name font-weight-bold mb-1">
              {{ printingMember.user.first_name }} {{ printingMember.user.last_name }}
            </h2>
            <p class="n_email text-muted mb-2">{{ printingMember.user.email }}</p>
            <span class="badge badge-primary px-3 py-2 text-white" style="font-size: 1rem; background-color: #7f56d9;">
              {{ localization('Topic') }}: {{ groupTopic }}
            </span>
            <div class="mt-4 text-left">
              <UserPattern :userScore="printingMember.user" :withTitle="false" :hideActions="true"></UserPattern>
            </div>
            <div v-if="notebooksToShow.length > 0" class="mt-4 text-left">
              <div 
                v-for="subtopic in notebooksToShow" 
                :key="'print-notebook-' + subtopic.id" 
                class="mb-4 student_notebook_print_card p-3 border rounded"
              >
                <h3 class="notebook_card_title font-weight-bold text-dark mb-3" style="font-size: 1.3rem; border-left: 4px solid #7f56d9; padding-left: 10px;">
                  {{ subtopic.student_notebook_title || subtopic.name || localization('Students Notebook') }}
                </h3>
                <div 
                  class="student_notebook_wrapper"
                  v-html="formatNotebookHtml(subtopic.student_notebook)"
                ></div>
              </div>
            </div>
            <!-- Personalize Profile Print Section -->
            <div v-if="userAttributes" class="global_theme_ui_collapse mt-4 text-left">
              <h3 class="font-weight-bold text-dark mb-2" style="font-size: 1.25rem;">
                {{ localization("Personalize your profile") }}
              </h3>
              <div
                class="main_child mb-3 p-3 rounded"
                v-for="(attribute, key) in userAttributes"
                :key="'print-attr-' + key"
                :style="`border: 2px solid ${getProgressColor(key)}`"
              >
                <div class="custom_header_of_collapse d-flex align-items-center w-100 pb-2 border-bottom mb-2">
                  <span class="title_word font-weight-bold text-dark" style="font-size: 1rem;">
                    {{ localization(key) }} - {{ localization(patternKey(key)) }}
                  </span>
                </div>
                <div class="p-2">
                  <div
                    class="checkboxes_wrapper d-flex align-items-center mb-2"
                    v-for="(item, index) in attribute"
                    :key="'print-item-' + index"
                    v-if="item && item.title"
                  >
                    <i v-if="isAttributeSelected(item.id)" class="fa fa-check-square-o text-primary mr-2" style="color: #7f56d9;"></i>
                    <i v-else class="fa fa-square-o text-muted mr-2"></i>
                    <span :class="{'text-primary font-weight-bold': isAttributeSelected(item.id), 'text-dark font-weight-medium': !isAttributeSelected(item.id)}" style="font-size: 0.9rem;">{{ item.title }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div 
            v-for="subtopic in printingMember.subtopics" 
            :key="subtopic.id"
            class="print-page-break mb-5"
          >
            <div class="subtopic-print-header mb-4">
              <h3 class="font-weight-bold text-dark mb-1" style="font-size: 1.5rem; border-left: 4px solid #7f56d9; padding-left: 10px;">
                {{ subtopic.name }}
              </h3>
              <p class="text-muted">{{ subtopic.description }}</p>
            </div>

            <div v-if="subtopic.components && subtopic.components.length > 0">
              <div 
                v-for="component in subtopic.components" 
                :key="component.id" 
                class="component-print-block mb-4 p-3 border rounded"
              >
                <h4 class="font-weight-bold text-secondary mb-2" style="font-size: 1.15rem;">
                  {{ component.title }}
                </h4>
                <div class="component-body mb-3 text-muted" v-if="component.body" v-html="component.body"></div>

                <div v-if="(component.question_cards && component.question_cards.length > 0) || (isStrategiesComponent(component) && component.strategies_cards && component.strategies_cards.length > 0)" class="mt-3">
                  <!-- Custom Strategies Component UI (Print) -->
                  <div v-if="isStrategiesComponent(component)" class="strategies-component-container">
                    <!-- Render strategies_cards if available -->
                    <template v-if="component.strategies_cards && component.strategies_cards.length > 0">
                      <div 
                        v-for="(strategy, sIndex) in component.strategies_cards" 
                        :key="strategy.id || sIndex"
                        class="strategy-card-item page-break-inside-avoid mb-4"
                      >
                        <div class="strategy-banner" :style="{ backgroundColor: getStrategyColor(strategy.pattern) }">
                          <div class="strategy-pattern-name">{{ strategy.pattern }}</div>
                          <div class="strategy-main-text" v-html="strategy.name"></div>
                        </div>
                        <div class="strategy-description-box" v-if="strategy.description">
                          <div class="strategy-description-text" v-html="formatHelpText(strategy.description)"></div>
                        </div>
                      </div>
                    </template>
                    <!-- Fallback to question_cards if strategies_cards is empty -->
                    <template v-else>
                      <div 
                        v-for="(qCard, qIndex) in component.question_cards" 
                        :key="qCard.id || qIndex"
                        class="strategy-card-item page-break-inside-avoid mb-4"
                      >
                        <div class="strategy-banner" :style="{ backgroundColor: getStrategyColor(parseStrategyCard(qCard).pattern) }">
                          <div class="strategy-pattern-name">{{ parseStrategyCard(qCard).pattern }}</div>
                          <div class="strategy-main-text" v-html="parseStrategyCard(qCard).mainTitle"></div>
                        </div>
                        <div class="strategy-description-box" v-if="parseStrategyCard(qCard).description">
                          <div class="strategy-description-text" v-html="formatHelpText(parseStrategyCard(qCard).description)"></div>
                        </div>
                        <div class="strategy-student-answer-box" v-if="qCard.student_answer || qCard.answer">
                          <div class="strategy-student-answer-title">{{ localization('Answer') }}</div>
                          <div class="strategy-student-answer-text">{{ qCard.student_answer || qCard.answer }}</div>
                        </div>
                      </div>
                    </template>
                  </div>

                  <!-- Default Question Cards UI (Print) -->
                  <template v-else>
                    <div v-for="(qCard, qIndex) in component.question_cards" :key="qCard.id" class="mb-4 page-break-inside-avoid">
                      <!-- MCQ Type -->
                      <div v-if="qCard.type === 'mcq' && qCard.choices && qCard.choices.length > 0">
                        <div class="d-flex align-items-start mb-2 pl-3 border-left" style="border-width: 3px !important; border-color: #7f56d9 !important;">
                          <span class="badge badge-secondary mr-2 mt-1 text-white" style="background-color: #7f56d9;">
                            {{ qIndex + 1 }}
                          </span>
                          <span class="font-weight-bold text-dark" style="font-size: 0.95rem;">
                            {{ qCard.question }}
                          </span>
                        </div>
                        <div class="pl-5 mt-2">
                          <div 
                            v-for="choice in qCard.choices" 
                            :key="choice.id" 
                            class="choice-row d-flex align-items-center mb-1 p-2 rounded border"
                            :style="qCard.student_answer === choice.id ? 'background-color: #f9f5ff; border-color: #7f56d9; border-left: 4px solid #7f56d9;' : 'background-color: #ffffff; border-color: #eaecf0;'"
                          >
                            <i v-if="qCard.student_answer === choice.id" class="fa fa-dot-circle-o text-primary mr-2" style="color: #7f56d9;"></i>
                            <i v-else class="fa fa-circle-o text-muted mr-2"></i>
                            <span :class="{'text-primary font-weight-bold': qCard.student_answer === choice.id, 'text-secondary': qCard.student_answer !== choice.id}">
                              {{ choice.text }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Essay Type -->
                      <div v-else class="essay-question-container">
                        <div class="essay-question-header">
                          <div class="essay-step-label">
                            Step {{ parseQuestionCard(qCard, qIndex).stepNumber }}: <strong>{{ parseQuestionCard(qCard, qIndex).stepName }}</strong>
                          </div>
                          <div class="essay-main-question">
                            {{ parseQuestionCard(qCard, qIndex).mainQuestion }}
                          </div>
                          <div class="essay-help-text" v-if="parseQuestionCard(qCard, qIndex).helpText" v-html="formatHelpText(parseQuestionCard(qCard, qIndex).helpText)">
                          </div>
                        </div>
                        <div class="essay-question-body">
                          <div class="essay-answer-preview">
                            {{ qCard.student_answer || qCard.answer || localization('No answer submitted yet.') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "ViewStudentAnswers",
  data() {
    return {
      loading: true,
      member: {},
      selected: [],
      groupName: "",
      groupTopic: "",
      schoolGrade: "",
      subtopicsAnswers: [],
      gradeNotebooks: [],
      printingMember: null,
      printMode: "all",
      userAttributes: null,
      activePanels: ["Sequence", "Precision", "Technical Reasoning", "Confluence"]
    };
  },
  computed: {
    subtopicsWithNotebook() {
      return this.subtopicsAnswers.filter(s => s && s.student_notebook);
    },
    notebooksToShow() {
      // Combine subtopic-matched notebooks with standalone grade-only notebooks
      const subtopicNotebooks = this.subtopicsWithNotebook;
      const gradeOnlyNotebooks = this.gradeNotebooks.map(n => ({
        id: 'grade-' + n.id,
        student_notebook: n.body || n.student_notebook,
        student_notebook_title: n.name
      }));
      return [...subtopicNotebooks, ...gradeOnlyNotebooks];
    },
    selectedAttributes() {
      if (!this.member || !this.member.selected_attributes) return [];
      let attrs = this.member.selected_attributes;
      if (Array.isArray(attrs)) {
        return attrs;
      }
      if (typeof attrs === "string") {
        try {
          let parsed = JSON.parse(attrs);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          return attrs.split(",").map(item => item.trim());
        }
      }
      return [];
    }
  },
  watch: {
    selectedAttributes: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val) && val.length > 0) {
          const list = [];
          val.forEach(id => {
            list.push(id);
            list.push(String(id));
            if (!isNaN(id)) list.push(Number(id));
          });
          this.selected = list;
        } else {
          this.selected = [];
        }
      }
    }
  },
  methods: {
    isAttributeSelected(id) {
      if (id == null) return false;
      const attrs = this.selectedAttributes;
      if (!Array.isArray(attrs)) return false;
      return attrs.some(x => String(x) === String(id));
    },
    patternKey(name) {
      const score = this.member || {};
      if (name === "Sequence") {
        if (score.sequence >= 25 && score.sequence <= 35) return "Use First";
        if (score.sequence >= 7 && score.sequence <= 17) return "Avoid";
        if (score.sequence >= 18 && score.sequence <= 24) return "As Needed";
      }
      if (name === "Precision") {
        if (score.precision >= 25 && score.precision <= 35) return "Use First";
        if (score.precision >= 7 && score.precision <= 17) return "Avoid";
        if (score.precision >= 18 && score.precision <= 24) return "As Needed";
      }
      if (name === "Technical Reasoning") {
        if (score.technical_reasoning >= 25 && score.technical_reasoning <= 35) return "Use First";
        if (score.technical_reasoning >= 7 && score.technical_reasoning <= 17) return "Avoid";
        if (score.technical_reasoning >= 18 && score.technical_reasoning <= 24) return "As Needed";
      }
      if (name === "Confluence") {
        if (score.confluence >= 25 && score.confluence <= 35) return "Use First";
        if (score.confluence >= 7 && score.confluence <= 17) return "Avoid";
        if (score.confluence >= 18 && score.confluence <= 24) return "As Needed";
      }
      return "";
    },
    getProgressColor(key) {
      switch (key) {
        case "Precision":
          return "#3c3";
        case "Technical Reasoning":
          return "#f93";
        case "Confluence":
          return "#c36";
        case "Sequence":
          return "#39c";
        default:
          return "#7f56d9";
      }
    },
    text_speech_audio(any_text) {
      if (!any_text) return;
      request({
        url: `/users/text/to/speech`,
        method: "post",
        data: { text: any_text },
        responseType: "blob"
      }).then(res => {
        let audioBlob = new Blob([res], { type: "audio/mpeg" });
        let audioURL = URL.createObjectURL(audioBlob);
        let audio = document.getElementById("audio-answers");
        var source = document.getElementById("audio-source-answers");
        if (audio && source) {
          source.src = audioURL;
          audio.load();
          audio.play();
        }
      }).catch(err => console.error("Text to speech error:", err));
    },
    getGradeTitle() {
      if (!this.schoolGrade) return "Grade 1 ELA";
      let str = String(this.schoolGrade).trim();
      if (!/grade/i.test(str)) {
        str = `Grade ${str}`;
      }
      if (!/ela/i.test(str)) {
        str = `${str} ELA`;
      }
      return str;
    },
    formatNotebookHtml(rawHtml) {
      if (!rawHtml) return "";
      let html = rawHtml;
      const studentName = this.member ? `${this.member.first_name || ''} ${this.member.last_name || ''}`.trim() : "___";
      const gradeTitle = this.getGradeTitle();
      const seq = (this.member && this.member.sequence != null) ? this.member.sequence : "___";
      const prec = (this.member && this.member.precision != null) ? this.member.precision : "___";
      const tech = (this.member && this.member.technical_reasoning != null) ? this.member.technical_reasoning : "___";
      const conf = (this.member && this.member.confluence != null) ? this.member.confluence : "___";

      // Replace student name
      if (html.includes('notebook-student-name')) {
        html = html.replace(/(<span[^>]*class="[^"]*notebook-student-name[^"]*"[^>]*>)(.*?)(<\/span>)/gi, `$1${studentName}$3`);
      } else {
        html = html.replace(/THIS NOTEBOOK BELONGS TO:?/gi, `THIS NOTEBOOK BELONGS TO: <span class="notebook-student-name" style="color: #7f56d9;">${studentName}</span>`);
      }
      html = html.replace(/\{\{\s*STUDENT_NAME\s*\}\}/g, studentName);

      // Replace grade title according to student level / group school_grade
      if (html.includes('notebook-grade-title')) {
        html = html.replace(/(<h2[^>]*class="[^"]*notebook-grade-title[^"]*"[^>]*>)(.*?)(<\/h2>)/gi, `$1${gradeTitle}$3`);
      } else {
        html = html.replace(/<h2([^>]*)>Grade\s*\d+\s*ELA<\/h2>/gi, `<h2$1 class="notebook-grade-title">${gradeTitle}</h2>`);
        html = html.replace(/Grade\s*\d+\s*ELA/gi, gradeTitle);
      }
      html = html.replace(/\{\{\s*SCHOOL_GRADE\s*\}\}/g, gradeTitle);

      // Replace class-based score placeholders
      html = html.replace(/(<span[^>]*class="[^"]*notebook-score-sequence[^"]*"[^>]*>)(.*?)(<\/span>)/gi, `$1${seq}$3`);
      html = html.replace(/(<span[^>]*class="[^"]*notebook-score-precision[^"]*"[^>]*>)(.*?)(<\/span>)/gi, `$1${prec}$3`);
      html = html.replace(/(<span[^>]*class="[^"]*notebook-score-technical_reasoning[^"]*"[^>]*>)(.*?)(<\/span>)/gi, `$1${tech}$3`);
      html = html.replace(/(<span[^>]*class="[^"]*notebook-score-confluence[^"]*"[^>]*>)(.*?)(<\/span>)/gi, `$1${conf}$3`);

      // Replace label-adjacent underlines
      html = html.replace(/(Sequence:?\s*<\/span>\s*<span[^>]*>)(___)(<\/span>)/gi, `$1${seq}$3`);
      html = html.replace(/(Precision:?\s*<\/span>\s*<span[^>]*>)(___)(<\/span>)/gi, `$1${prec}$3`);
      html = html.replace(/(Technical Reasoning:?\s*<\/span>\s*<span[^>]*>)(___)(<\/span>)/gi, `$1${tech}$3`);
      html = html.replace(/(Confluence:?\s*<\/span>\s*<span[^>]*>)(___)(<\/span>)/gi, `$1${conf}$3`);

      // Fallback bracket placeholders
      html = html.replace(/\{\{\s*SCORE_SEQUENCE\s*\}\}/g, seq);
      html = html.replace(/\{\{\s*SCORE_PRECISION\s*\}\}/g, prec);
      html = html.replace(/\{\{\s*SCORE_TECHNICAL_REASONING\s*\}\}/g, tech);
      html = html.replace(/\{\{\s*SCORE_CONFLUENCE\s*\}\}/g, conf);

      return html;
    },
    fetchData() {
      this.loading = true;
      const groupCardId = this.$route.params.groupCardId;
      const userId = parseInt(this.$route.params.userId);

      const pGroupCard = request({
        url: `/group_cards/show/${groupCardId}`,
        method: "get"
      });

      const pUser = request({
        url: `/users/show/${userId}`,
        method: "get"
      });

      Promise.all([pGroupCard, pUser]).then(([groupRes, userRes]) => {
        let memberData = {};
        const groupCard = groupRes && groupRes.groupCard ? groupRes.groupCard : null;

        if (groupCard) {
          this.groupName = groupCard.name || "";
          const firstTopic = groupCard.topicsGroupCards && groupCard.topicsGroupCards[0];
          this.groupTopic = firstTopic ? firstTopic.topic_card_name : "";
          this.schoolGrade = firstTopic && firstTopic.school_grade ? String(firstTopic.school_grade) : "";

          if (groupCard.usersGroupCards) {
            const foundMember = groupCard.usersGroupCards.find(m => (m.user_id === userId || m.id === userId));
            if (foundMember) {
              memberData = { ...foundMember };
            }
          }
        }

        if (userRes) {
          if (userRes.user) {
            memberData = { ...memberData, ...userRes.user };
            if (userRes.user.selected_attributes) {
              let rawAttrs = userRes.user.selected_attributes;
              let parsedAttrs = [];
              if (Array.isArray(rawAttrs)) {
                parsedAttrs = rawAttrs;
              } else if (typeof rawAttrs === 'string') {
                try {
                  parsedAttrs = JSON.parse(rawAttrs);
                } catch(e) {
                  parsedAttrs = rawAttrs.split(',').map(s => s.trim());
                }
              }
              const selList = [];
              parsedAttrs.forEach(id => {
                selList.push(id);
                selList.push(String(id));
                if (!isNaN(id)) selList.push(Number(id));
              });
              this.selected = selList;
            }
          }
          if (userRes.attributes) {
            this.userAttributes = userRes.attributes;
          }
        }

        this.member = memberData;

        // Always fetch notebooks by grade (includes grade-only notebooks with no subtopic)
        const pNotebooks = request({
          url: `/students_notebook/list`,
          method: "post",
          data: {
            school_grade: this.schoolGrade,
            limit: 100
          }
        }).catch(() => null);

        // Fetch subtopics answers if available
        const subtopicIds = (groupRes && groupRes.groupCard && groupRes.groupCard.subTopicsCards)
          ? groupRes.groupCard.subTopicsCards.map(s => s.id)
          : [];

        const pSubtopics = subtopicIds.map(id => {
          return request({
            url: `/sub_topics_card/show/${id}`,
            method: "get",
            params: { user_id: userId, school_grade: this.schoolGrade }
          });
        });

        Promise.all([...pSubtopics, pNotebooks]).then(responses => {
          const notebookRes = responses[responses.length - 1];
          const dynamicNotebooks = notebookRes && (notebookRes.studentsNotebooks || notebookRes.data) ? (notebookRes.studentsNotebooks || notebookRes.data) : [];

          // Helper: normalize grade string for comparison
          const cleanGrade = g => g ? String(g).toLowerCase().replace(/grade\s*/i, '').trim() : '';
          const currentGrade = cleanGrade(this.schoolGrade);

          const subtopicResponses = responses.slice(0, responses.length - 1);
          this.subtopicsAnswers = subtopicResponses.map(r => {
            const subtopic = r && r.subTopicsCard;
            if (subtopic) {
              // Match notebooks that are linked to this specific subtopic
              const match = dynamicNotebooks.find(n => {
                const notebookGrade = cleanGrade(n.school_grade);
                const gradeMatches = !currentGrade || !notebookGrade || notebookGrade === currentGrade;
                return n.sub_topic_card_id && String(n.sub_topic_card_id) === String(subtopic.id) && gradeMatches;
              });

              if (match && (match.body || match.student_notebook)) {
                subtopic.student_notebook = match.body || match.student_notebook;
                subtopic.student_notebook_title = match.name || subtopic.student_notebook_title || subtopic.name;
              } else if (!subtopic.student_notebook_title) {
                subtopic.student_notebook_title = subtopic.name;
              }
            }
            return subtopic;
          }).filter(Boolean);

          // Collect grade-only notebooks (no sub_topic_card_id) that match the student's grade
          this.gradeNotebooks = dynamicNotebooks.filter(n => {
            if (n.sub_topic_card_id) return false; // skip subtopic-linked ones
            if (!currentGrade) return true;
            const notebookGrade = cleanGrade(n.school_grade);
            return !notebookGrade || notebookGrade === currentGrade;
          });

          this.loading = false;
        }).catch(err => {
          console.error(err);
          this.loading = false;
        });
      }).catch(err => {
        console.error("Error fetching student profile data:", err);
        this.loading = false;
      });
    },
    _executePrint() {
      // Directly reveal the print container using inline !important styles.
      // inline style.setProperty(..., 'important') has higher cascade priority than
      // any stylesheet !important rule (including scoped ones), guaranteeing visibility.
      const printEl = this.$el.querySelector('.the_main_container_for_print_all_pdf');
      if (!printEl) {
        console.warn('[Print] Container not found in DOM');
        return;
      }

      const SHOW_PROPS = {
        position: 'static',
        left: 'auto',
        top: 'auto',
        width: '100%',
        height: 'auto',
        overflow: 'visible',
        visibility: 'visible',
        display: 'block'
      };
      Object.entries(SHOW_PROPS).forEach(([prop, val]) => {
        printEl.style.setProperty(prop, val, 'important');
      });

      // Hide the regular page content (sibling of the print container)
      const screenContent = this.$el.querySelector('.answers_container');
      if (screenContent) screenContent.style.setProperty('display', 'none', 'important');

      // Hide global chrome (sidebar, navbar) via an injected global style tag
      this._injectPrintStyle();

      const originalTitle = document.title;
      document.title = '';

      const cleanup = () => {
        document.title = originalTitle;
        // Restore screen content
        if (screenContent) screenContent.style.removeProperty('display');
        // Remove inline overrides from print container
        Object.keys(SHOW_PROPS).forEach(prop => printEl.style.removeProperty(prop));
        this.printingMember = null;
        this._removePrintStyle();
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);

      // requestAnimationFrame ensures the browser repaints the updated layout
      // before the print dialog opens, so the content is visible in the preview.
      requestAnimationFrame(() => {
        window.print();
      });
    },
    _injectPrintStyle() {
      // Hides the app chrome (sidebar, navbar, layout wrappers) which are
      // outside this component and cannot be targeted by scoped CSS.
      const existing = document.getElementById('__student-answers-print-style');
      if (existing) existing.remove();

      const style = document.createElement('style');
      style.id = '__student-answers-print-style';
      style.textContent = `
        @media print {
          .sidebar, .navbar, .footer,
          #app > .wrapper > .sidebar,
          #app > .wrapper > .navbar {
            display: none !important;
          }
          html, body, #app, .wrapper, .main-panel, .content, .main_profile_wrapper {
            position: static !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }
          @page { size: auto; margin: 8mm 10mm 12mm 10mm; }
        }
      `;
      document.head.appendChild(style);
    },
    _removePrintStyle() {
      const style = document.getElementById('__student-answers-print-style');
      if (style) style.remove();
    },
    printAnswers() {
      if (!this.member || this.subtopicsAnswers.length === 0) return;

      this.printMode = 'all';
      this.printingMember = {
        user: this.member,
        subtopics: this.subtopicsAnswers
      };

      // First tick: Vue flushes the v-if="printingMember" block into the DOM.
      // Second tick: child components (UserPattern, PLP) complete their render cycles.
      this.$nextTick(() => {
        this.$nextTick(() => {
          this._executePrint();
        });
      });
    },
    printNotebook() {
      if (!this.member || this.notebooksToShow.length === 0) return;

      this.printMode = 'notebook';
      this.printingMember = {
        user: this.member,
        subtopics: this.subtopicsAnswers
      };

      this.$nextTick(() => {
        this.$nextTick(() => {
          this._executePrint();
          // printMode is reset to 'all' inside _executePrint's afterprint cleanup
          // so we hook into afterprint to do it after cleanup
          const resetMode = () => {
            this.printMode = 'all';
            window.removeEventListener('afterprint', resetMode);
          };
          window.addEventListener('afterprint', resetMode);
        });
      });
    },
    goBack() {
      this.$router.push(`/data_cards/${this.$route.params.groupCardId}`);
    },
    getScoreLabel(score) {
      if (score == null) return '-';
      const val = parseInt(score);
      if (val >= 25 && val <= 35) {
        return this.localization('Use First');
      } else if (val >= 7 && val <= 17) {
        return this.localization('Avoid');
      } else if (val >= 18 && val <= 24) {
        return this.localization('As Needed');
      }
      return '-';
    },
    getPrintDate() {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },
    isStrategiesComponent(component) {
      if (!component) return false;
      if (component.title && (component.title.toLowerCase().includes("strategies") || component.title.toLowerCase().includes("strategy"))) {
        return true;
      }
      if (component.question_cards && component.question_cards.length > 0) {
        const firstQ = component.question_cards[0].question || "";
        const name = this.getStrategyPattern(firstQ);
        if (name) return true;
      }
      return false;
    },
    getStrategyPattern(questionText) {
      if (!questionText) return "";
      const text = questionText.toLowerCase();
      if (text.includes("technical reasoning")) {
        return "Technical Reasoning";
      }
      if (text.includes("sequence")) {
        return "Sequence";
      }
      if (text.includes("precision")) {
        return "Precision";
      }
      if (text.includes("confluence")) {
        return "Confluence";
      }
      return "";
    },
    parseStrategyCard(qCard) {
      const text = qCard.question || "";
      const pattern = this.getStrategyPattern(text) || "Sequence";
      
      // Remove prefixes like "Step X: Pattern Name: " or "Step X - Pattern Name - "
      let cleanText = text.replace(/^\s*Step\s*\d+\s*[:\-]?\s*(Technical Reasoning|Sequence|Precision|Confluence)\s*[:\-]?\s*/i, "");
      
      if (cleanText === text) {
        cleanText = text.replace(/^\s*Step\s*\d+\s*[:\-]?\s*/i, "");
        const patternRegex = new RegExp(`^\\s*${pattern}\\s*[:\\-]?\\s*`, 'i');
        cleanText = cleanText.replace(patternRegex, "");
      }

      let mainTitle = cleanText;
      let description = "";
      
      const newlineIndex = cleanText.indexOf("\n");
      if (newlineIndex !== -1) {
        mainTitle = cleanText.substring(0, newlineIndex).trim();
        description = cleanText.substring(newlineIndex).trim();
      } else {
        const avoidIndex = cleanText.toLowerCase().indexOf("if you avoid");
        if (avoidIndex !== -1) {
          mainTitle = cleanText.substring(0, avoidIndex).trim();
          description = cleanText.substring(avoidIndex).trim();
        }
      }

      return {
        pattern,
        mainTitle,
        description
      };
    },
    getStrategyColor(pattern) {
      if (!pattern) return "#7f56d9";
      const pat = pattern.toLowerCase().trim();
      if (pat.includes("sequence")) return "#1d5ea8";
      if (pat.includes("precision")) return "#388225";
      if (pat.includes("technical reasoning") || pat === "technical") return "#b84e0c";
      if (pat.includes("confluence")) return "#bc0587";
      return "#7f56d9";
    },
    formatHelpText(text) {
      if (!text) return "";
      let html = text.replace(/\n/g, "<br />");
      const colors = {
        "Technical Reasoning": "#b84e0c",
        Sequence: "#1d5ea8",
        Precision: "#388225",
        Confluence: "#bc0587"
      };
      
      Object.keys(colors).forEach(pattern => {
        const color = colors[pattern];
        const regex = new RegExp(`(${pattern})`, 'gi');
        html = html.replace(regex, `<span class="pattern-colored" style="color: ${color}; font-weight: bold;">$1</span>`);
      });
      
      return html;
    }
  },
  created() {
    this.fetchData();
  }
};
</script>

<style lang="scss" scoped>
.main_profile_wrapper {
  background: #f8f9fa;
  width: 100%;
  max-width: 100%;
  padding: 30px;
}
.header_bar {
  border-bottom: 1px solid #eaecf0;
  padding-bottom: 20px;
}
.avatar_icon {
  background-color: #7f56d9;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 1.5rem;
}
.member_info_card {
  border-radius: 12px;
  background: #ffffff;
}
.subtopic_block {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #eaecf0;
}
.subtopic_title_bar {
  background: #f9f5ff;
  border-left: 5px solid #7f56d9;
}
.component_card {
  border-radius: 10px;
  border: 1px solid #f0f2f5;
  background-color: #ffffff;
}
.bg-light-gray {
  background-color: #fcfcfd;
}
.q_badge {
  background-color: #7f56d9;
  font-size: 0.85rem;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.text_question {
  font-size: 1rem;
  line-height: 1.5;
}
.gap-2 {
  gap: 10px;
}
.gap-3 {
  gap: 15px;
}
.main_title {
  color: #101828;
  font-weight: 600;
  font-size: 20px;
}
.content_grid {
  width: 100%;
  max-width: 100%;
}
.answers_container {
  max-width: 1500px;
  margin: 0 auto;
  width: 100%;
}
.component_body {
  width: 100%;
  line-height: 1.6;
  color: #475467;
  
  ::v-deep {
    table {
      width: 100% !important;
      max-width: 100% !important;
      table-layout: auto !important;
      border-collapse: collapse !important;
    }
    img, iframe, video {
      max-width: 100% !important;
      height: auto !important;
    }
    ul, ol {
      padding-left: 20px;
    }
  }
}
.student_notebook_wrapper {
  width: 100%;
  box-sizing: border-box;
}

::v-deep .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
  background-color: #7f56d9 !important;
  border-color: #7f56d9 !important;
}
::v-deep .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner::after {
  border-color: #ffffff !important;
  transform: rotate(45deg) scaleY(1) !important;
  display: block !important;
}
::v-deep .el-checkbox__input.is-disabled.is-checked + .el-checkbox__label {
  color: #7f56d9 !important;
  font-weight: 600 !important;
}
::v-deep .el-checkbox__input.is-disabled + .el-checkbox__label {
  color: #344054 !important;
  opacity: 1 !important;
}

/* Strategies Component Styles */
.strategies-component-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.strategy-card-item {
  width: 100%;
}

.strategy-banner {
  padding: 16px 20px;
  color: #ffffff;
  border-radius: 4px;
}

.strategy-pattern-name {
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.strategy-main-text {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}

.strategy-description-box {
  margin-left: 40px;
  margin-top: 12px;
  padding-left: 20px;
  border-left: 2px solid #e5e7eb;
}

.strategy-description-text {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #475467;
}

.strategy-student-answer-box {
  margin-left: 40px;
  margin-top: 12px;
  padding: 15px;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px dashed #eaecf0;
}

.strategy-student-answer-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #344054;
  margin-bottom: 4px;
}

.strategy-student-answer-text {
  font-size: 0.95rem;
  color: #475467;
  line-height: 1.5;
}
</style>

<style lang="scss" scoped>
/* Keep the print block out of screen view WITHOUT using display:none.
   display:none can silently fail to be overridden in @media print with scoped CSS.
   Off-screen + visibility:hidden keeps it rendered but invisible. */
.print-only-block {
  position: absolute !important;
  left: -9999px !important;
  top: -9999px !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
}

@media print {
  /* Reveal the print container - reset off-screen properties */
  .print-only-block {
    position: static !important;
    left: auto !important;
    top: auto !important;
    width: 100% !important;
    height: auto !important;
    overflow: visible !important;
    visibility: visible !important;
    display: block !important;
  }

  /* Reset layout constraints for print */
  html, body, #app, .wrapper, .main-panel, .content, .main_profile_wrapper {
    position: static !important;
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: #fff !important;
    box-shadow: none !important;
  }

  @page {
    size: auto;
    margin: 8mm 10mm 12mm 10mm;
  }

  body {
    background-color: #fff !important;
    counter-reset: page;
  }

  .the_main_container_for_print_all_pdf {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    background: #fff !important;
  }

  .main_container {
    width: 100% !important;
    max-width: 100% !important;
    padding: 1mm 1mm !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .main_auth_user_info {
    margin-bottom: 15px !important;
    padding-bottom: 10px !important;
  }

  .subtopic-print-header {
    margin-bottom: 10px !important;
    page-break-after: avoid !important;
    break-after: avoid-page !important;
  }

  .component-print-block {
    width: 100% !important;
    max-width: 100% !important;
    padding: 10px !important;
    margin-bottom: 12px !important;
    box-sizing: border-box !important;
  }

  .component-print-block + .component-print-block {
    page-break-before: always !important;
    break-before: page !important;
  }

  .component-body,
  .component-body * {
    max-width: 100% !important;
  }

  .component-body table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: auto !important;
    border-collapse: collapse !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .component-body img {
    max-width: 100% !important;
    height: auto !important;
  }

  .print-page-break {
    page-break-after: always !important;
    break-after: page !important;
    counter-increment: page;
  }

  .print-page-break.mb-5 {
    margin-bottom: 15px !important;
  }

  .page-break-inside-avoid {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Ensure backgrounds render in print */
  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
}
</style>
